import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';

interface MeResponse {
  uid: string;
  email: string;
}

interface HealthResponse {
  status: string;
  service: string;
  timestamp: string;
}

export default function Dashboard() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [meRes, healthRes] = await Promise.all([
          apiFetch<MeResponse>('/api/me'),
          // /api/health is unauthenticated, but we use the same wrapper for simplicity
          fetch(`${import.meta.env.VITE_API_BASE_URL}/api/health`).then((r) => r.json()),
        ]);
        setMe(meRes);
        setHealth(healthRes);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(`${err.status}: ${err.message}`);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Milestone 1 placeholder. Cost data will appear here in Milestone 2.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Authenticated identity (/api/me)">
          {me ? (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-slate-500">Email</dt>
                <dd className="font-medium">{me.email}</dd>
              </div>
              <div>
                <dt className="text-slate-500">UID</dt>
                <dd className="font-mono text-xs break-all">{me.uid}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-slate-400">Loading...</p>
          )}
        </Card>

        <Card title="Backend health (/api/health)">
          {health ? (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-slate-500">Status</dt>
                <dd className="font-medium text-emerald-600">{health.status}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Service</dt>
                <dd className="font-mono text-xs">{health.service}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Timestamp</dt>
                <dd className="font-mono text-xs">{health.timestamp}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-slate-400">Loading...</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      {children}
    </div>
  );
}
